import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

/**
 * In-app Signal Bar — a single short AI-crafted line that orients,
 * narrows, restores, reinforces, or closes the loop based on user state.
 * Not a popup, not a banner. Tap → routes to the relevant flow.
 */
const SignalBar = () => {
  const navigate = useNavigate();
  const [signal, setSignal] = useState(null);
  const [celebrating, setCelebrating] = useState(false);

  const fetchSignal = useCallback(async () => {
    try {
      const res = await api.get('/signal/current');
      setSignal(res.data);
    } catch (error) {
      setSignal(null);
    }
  }, []);

  useEffect(() => {
    fetchSignal();
    const t = setInterval(fetchSignal, 120000);
    return () => clearInterval(t);
  }, [fetchSignal]);

  // Cross-component reward cue: Direction ✓ taps anywhere dispatch
  // `signal:celebrate` — the dot briefly flashes soft green.
  useEffect(() => {
    const handler = () => {
      setCelebrating(true);
      const t = setTimeout(() => setCelebrating(false), 4000);
      return () => clearTimeout(t);
    };
    window.addEventListener('signal:celebrate', handler);
    return () => window.removeEventListener('signal:celebrate', handler);
  }, []);

  const handleTap = () => {
    if (!signal) return;
    const route = signal.route || '/navigator';
    api.post('/signal/tap', {
      message: signal.message,
      purpose: signal.purpose,
      reason: signal.reason,
      route: signal.route,
      intent: signal.intent,
    }).catch(() => {});
    navigate(route, { state: { signal } });
  };

  if (!signal?.message) return null;

  // Color theme shifts during celebration — soft emerald pulse ≈ "the system noticed you moved"
  const dotColor = celebrating ? '#34D399' : '#D4AF37';
  const textColor = celebrating ? 'text-emerald-300/90 group-hover:text-emerald-300' : 'text-[#D4AF37]/90 group-hover:text-[#D4AF37]';
  const caretColor = celebrating ? 'text-emerald-300/50 group-hover:text-emerald-300/80' : 'text-[#D4AF37]/40 group-hover:text-[#D4AF37]/80';

  return (
    <AnimatePresence mode="wait">
      <motion.button
        key={signal.message}
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.5 }}
        onClick={handleTap}
        className="group w-full flex items-center justify-center gap-3 py-3 px-6 text-center"
        data-testid="signal-bar"
        data-celebrating={celebrating ? 'true' : 'false'}
        aria-label="In-app signal — tap to act"
      >
        {/* Pulsing orb — gold normally, soft green when celebrating */}
        <span className="relative inline-flex items-center justify-center shrink-0">
          <span
            className="absolute inline-flex h-2 w-2 rounded-full opacity-60 animate-ping"
            style={{ backgroundColor: dotColor, transition: 'background-color 400ms' }}
          />
          <span
            className="relative inline-flex h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: dotColor, transition: 'background-color 400ms' }}
          />
        </span>

        <span className={`font-serif text-sm sm:text-base transition-colors leading-snug tracking-wide ${textColor}`}>
          {signal.message}
        </span>

        <span
          className={`transition-colors text-xs ${caretColor}`}
          aria-hidden="true"
        >
          →
        </span>
      </motion.button>
    </AnimatePresence>
  );
};

export default SignalBar;
